import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {addStyle, View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {
    border,
    font,
    semanticColor,
    sizing,
} from "@khanacademy/wonder-blocks-tokens";
import {a11yStyles} from "@khan/shared-styles";

// UI components
import Pagination from "./pagination.tsx";
import {SortableHeader} from "./sortable-header.tsx";
import type {Column} from "./types.ts";
import usePagination from "./use-pagination.ts";
// hooks
import useSortable from "./use-sortable.ts";
// helpers
import {getValueFromKey} from "./utils.ts";

const StyledCaption = addStyle("caption");
const StyledTable = addStyle("table");
const StyledTr = addStyle("tr");
const StyledTd = addStyle("td");
const StyledTh = addStyle("th");

interface Props<T> {
    /**
     * The data to render in the table.
     * NOTE: We use generic types here to ensure that the data passed in is of
     * the same type as the data in the rows.
     */
    data: Array<T>;
    /**
     * The columns to include in the table.
     */
    columns: Array<Column<T>>;
    /**
     * The caption associated with the table. This is used to describe the table
     * to screen readers.
     */
    caption?: string;
    /**
     * The summary row for the table. This row will not be sortable or paginated.
     */
    summaryRow?: T;
    /**
     * An identifier for the table. This is used to associate the table with the
     * caption.
     */
    id?: string;
    /**
     * The default sort key for the table. If set, it will get the order from
     * the column info.
     */
    defaultSortKey?: string;
    /**
     * The number of items to show per page.
     */
    pageSize?: number;
    /**
     * Whether to render the pagination after the table.
     */
    paginationAfterTable?: boolean;
    /**
     * Custom styles for the table.
     */
    tableStyle?: StyleType;
    /**
     * Custom styles for the rows.
     */
    rowStyle?: StyleType;
    /**
     * Custom styles for the table.
     */
    headerStyle?: StyleType;
    /**
     * Custom cell styles for the table.
     */
    cellStyles?: StyleType;
    /**
     * A function to call when the page changes. This is useful for
     * server-side pagination. To check the page number synchronously,
     * just return `Promise.resolve()` from the callback.
     * @param page The new page number.
     * @returns A promise that resolves before the page changes.
     * @example
     * ```tsx
     * const {data, cursor} = useData();
     * const pageSize = 10;
     * const fullPagesLoaded = Math.ceil(data.length / pageSize);
     *
     * <SimpleTable
     *    data={data}
     *    columns={columns}
     *    pageSize={pageSize}
     *    onPageChange={async (nextPage) => {
     *        // If the next page is last page and the cursor is not null,
     *        // fetch the next chunk of data.
     *        if (nextPage >= fullPagesLoaded && cursor) {
     *            return await fetchData({cursor});
     *        }
     *   }}
     * />
     * ```
     */
    onPageChange?: (nextPage: number) => Promise<void>;
    /**
     * The current page to display in the table.
     * This allows the parent component to control the pagination state.
     */
    currentPage?: number;
    /**
     * For row pagination.
     * Whether there is more data to load.
     * This is used to determine if more data can be fetched so pagination can
     * can announce that total pages is uncertain.
     */
    hasMorePages?: boolean;
    /**
     * Optional custom header rows to render at the top of the <thead> before the main header row.
     */
    customHeaderRows?: React.ReactNode[];
    /**
     * Whether the table is loading.
     */
    isTableLoading?: boolean;
}

/**
 * Renders a simple table that supports sorting and pagination.
 */
export default function SimpleTable<T>({
    data,
    caption,
    columns,
    defaultSortKey,
    id = "table-id",
    pageSize,
    summaryRow,
    rowStyle,
    headerStyle,
    tableStyle,
    paginationAfterTable,
    cellStyles,
    onPageChange,
    currentPage,
    hasMorePages,
    customHeaderRows,
    isTableLoading,
}: Props<T>): React.ReactElement {
    // ---Sorting---
    const {onSort, sort, sortedData} = useSortable(
        data,
        columns,
        defaultSortKey,
    );
    const isSortable = columns.some((column) => column.sortable);

    // Use sortedData only if there are sortable columns, otherwise use data directly
    const displayData = isSortable ? sortedData : data;

    // Ensure that the page size is either a finite, positive number or
    // POSITIVE_INFINITY (i.e. no paginiation). So null, undefined, NaN, 0, any
    // negative number, or negative infinity will result in
    // no pagination -- indicated by positive infinity.
    const validatedPageSize: number =
        pageSize && pageSize > 0 ? pageSize : Number.POSITIVE_INFINITY;

    // --- Row pagination---
    const {setPage, page, pageData} = usePagination(
        displayData,
        currentPage || 1,
        validatedPageSize,
    );

    const totalItems = data.length;

    const [loading, setLoading] = React.useState(false);
    const handlePageChange = React.useCallback(
        async (newPage: number) => {
            if (onPageChange) {
                setLoading(true);
                await onPageChange(newPage);
                setLoading(false);
            }
            setPage(newPage);
        },
        [onPageChange, setPage],
    );

    const paginationArgs = React.useMemo(
        () =>
            ({
                id: `${id}-pagination`,
                page,
                pageSize: validatedPageSize,
                onPageChange: handlePageChange,
                total: totalItems,
                loading,
                hasMorePages: hasMorePages,
            }) satisfies React.ComponentProps<typeof Pagination>,
        [
            id,
            page,
            validatedPageSize,
            handlePageChange,
            totalItems,
            hasMorePages,
            loading,
        ],
    );

    return (
        /**
         * Allows annoucing the table to screen readers.
         * @see https://adrianroselli.com/2017/11/a-responsive-accessible-table.html#ResponsiveScrollingKeyboard
         */
        <View role="region" aria-labelledby={`${id}-caption`}>
            <View tabIndex={0} style={styles.wrapper}>
                <StyledTable
                    id={id}
                    style={[styles.table, tableStyle]}
                    aria-roledescription={
                        isTableLoading
                            ? t`Loading ${caption ?? "table"}`
                            : undefined
                    }
                    aria-busy={isTableLoading}
                >
                    {/**
                     * Allows screen readers to read the table caption, so that
                     * they know what the table is about.
                     * @see https://www.w3.org/TR/WCAG20-TECHS/H39
                     */}
                    {caption && (
                        <StyledCaption
                            style={a11yStyles.srOnly}
                            id={`${id}-caption`}
                        >
                            {caption}
                            {isSortable && (
                                <>
                                    {" "}
                                    {t`Column headers with buttons are sortable.`}
                                </>
                            )}
                        </StyledCaption>
                    )}
                    <thead>
                        {/* Render custom header rows if provided */}
                        {customHeaderRows &&
                            customHeaderRows.map((row, idx) =>
                                React.cloneElement(row as React.ReactElement, {
                                    key: `custom-header-row-${idx}`,
                                }),
                            )}

                        <StyledTr style={styles.sticky}>
                            {columns.map((column, i) => {
                                const isBeingSorted =
                                    sort?.orderBy === column.dataKey;
                                const sortOrder = isBeingSorted
                                    ? sort?.order
                                    : "none";

                                // To comply with WCAG 1.3.1, we should use the `scope`
                                // attribute to explicitly define the relationship between
                                // header cells and data cells
                                // @see https://www.w3.org/TR/WCAG20-TECHS/H63.html
                                return (
                                    <StyledTh
                                        key={i}
                                        aria-sort={sortOrder}
                                        scope="col"
                                        style={[
                                            styles.tableHeader,
                                            i === columns.length - 1 &&
                                                styles.rightAlign,
                                            column.style,
                                            headerStyle,
                                        ]}
                                    >
                                        {column.sortable && sort ? (
                                            <SortableHeader
                                                column={column}
                                                onSort={onSort}
                                                sort={sort}
                                            />
                                        ) : (
                                            column.label
                                        )}
                                    </StyledTh>
                                );
                            })}
                            {pageSize && !paginationAfterTable && (
                                <StyledTh
                                    style={styles.tableHeader}
                                    scope="col"
                                >
                                    <Pagination {...paginationArgs} />
                                </StyledTh>
                            )}
                        </StyledTr>
                    </thead>
                    <tbody>
                        {summaryRow && (
                            <StyledTr
                                key="summary"
                                style={[styles.row, rowStyle]}
                            >
                                {columns.map((column, i) => {
                                    const CellComponent = column.isHeader
                                        ? StyledTh
                                        : StyledTd;
                                    return (
                                        <CellComponent
                                            key={`summary-${i}`}
                                            scope={
                                                column.isHeader
                                                    ? "row"
                                                    : undefined
                                            }
                                            style={[
                                                i === columns.length - 1 &&
                                                    styles.rightAlign,
                                                styles.cell,
                                                cellStyles,
                                            ]}
                                        >
                                            {i === 0 && (
                                                <View
                                                    id={`${id}-summary`}
                                                    style={a11yStyles.srOnly}
                                                >
                                                    {t`Summary row`}
                                                </View>
                                            )}
                                            {column.cell
                                                ? column.cell(summaryRow)
                                                : getValueFromKey(
                                                      summaryRow,
                                                      column.dataKey ?? "",
                                                  )}
                                        </CellComponent>
                                    );
                                })}
                            </StyledTr>
                        )}

                        {pageData.map((row, idx) => (
                            <StyledTr
                                key={idx}
                                style={[
                                    idx !== pageData.length - 1 && styles.row,
                                    rowStyle,
                                ]}
                            >
                                {columns.map((column, i) => {
                                    const CellComponent = column.isHeader
                                        ? StyledTh
                                        : StyledTd;
                                    return (
                                        <CellComponent
                                            key={i}
                                            scope={
                                                column.isHeader
                                                    ? "row"
                                                    : undefined
                                            }
                                            style={[
                                                i === columns.length - 1 &&
                                                    styles.rightAlign,
                                                styles.cell,
                                                column.style,
                                                cellStyles,
                                            ]}
                                        >
                                            {column.cell
                                                ? column.cell(row)
                                                : getValueFromKey(
                                                      row,
                                                      column.dataKey ?? "",
                                                  )}
                                        </CellComponent>
                                    );
                                })}
                            </StyledTr>
                        ))}
                    </tbody>
                </StyledTable>
            </View>
            {pageSize && paginationAfterTable && (
                <View>
                    <Pagination {...paginationArgs} />
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        width: "100%",
        overflowX: "auto",
        // Border applied to the wrapper so that the radius is applied
        border: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
        borderRadius: border.radius.radius_120,
    },
    table: {
        borderCollapse: "collapse",
        tableLayout: "fixed",
    },
    tableHeader: {
        paddingInline: sizing.size_240,
        paddingBlock: sizing.size_140,
        textAlign: "start",
        borderBlockEnd: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
        background: semanticColor.core.background.base.subtle,
        fontWeight: font.weight.semi,
    },
    sticky: {
        backgroundColor: semanticColor.core.background.base.default,
        position: "sticky",
        top: 0,
        // Prevents the sticky header from overlapping the table data.
        zIndex: 1,
    },
    row: {
        borderBlockEnd: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
    },
    cell: {
        padding: sizing.size_240,
        paddingBlock: sizing.size_140,
        verticalAlign: "middle",
    },
    rightAlign: {
        textAlign: "end",
    },
});
